<template>
  <v-container
    id="reports"
    fluid
    tag="section"
    class="reports-container pa-0 pa-md-3"
  >
    <div class="reports-content">
      <!-- Controls Card -->
      <v-card rounded="lg" elevation="2" class="mb-4 pa-4">
        <v-row>
          <v-col cols="12" md="3" >
            <v-select
              :items="storeOptions"
              v-model="selected_data_source_id"
              @change="fetchData"
              outlined
              dense
              hide-details
              label="Store"
              item-value="id"
              item-text="name"
              class="store-selector"
            />
          </v-col>
          <v-col cols="12" md="7" class="d-flex" style="gap:1em">
            <v-select
              v-model="selected_date_range"
              :items="date_ranges"
              outlined
              dense
              hide-details
              label="Date Range"
              item-value="value"
              item-text="text"
              class="date-range-selector"
              @change="handleDateRangeChange"
            />
            <template v-if="selected_date_range === 'custom'">
            <v-menu
              v-model="datePickerMenu1"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="customStartDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  hide-details
                  dense
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="customStartDate"
                @input="datePickerMenu1 = false; validateAndFetch()"
                no-title
                first-day-of-week="1"
                scrollable
              ></v-date-picker>
            </v-menu>
            <v-menu
              v-model="datePickerMenu2"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="customEndDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="customEndDate"
                first-day-of-week="1"
                @input="datePickerMenu2 = false; validateAndFetch()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
        </template>
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn
              color="primary"
              @click="downloadCSV"
              :disabled="loading || !fulfillments.length || !has_analytics"
              class="ml-auto"
            >
              <v-icon left>mdi-download</v-icon>
              Download CSV
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Loading indicator -->
      <div v-if="loading" class="loading-container">
        <v-progress-circular
          indeterminate
          :size="64"
          width="5"
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <div class="text-body-1 grey--text text--darken-1">
          Loading report data...
        </div>
      </div>

      <template v-else>
        <!-- Upgrade prompt when plan lacks analytics -->
        <v-alert
          v-if="!has_analytics"
          type="info"
          outlined
          class="mb-4 upgrade-alert"
        >
          <div class="d-flex align-center flex-wrap" style="gap: 1em">
            <span class="flex-grow-1">Your current plan does not include analytics. Upgrade to see full reports, insights and CSV exports.</span>
            <v-btn color="primary" depressed to="/auth/plans">Upgrade plan to access analytics</v-btn>
          </div>
        </v-alert>

        <!-- Summary Cards -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-card 
              rounded="lg" 
              elevation="2" 
              class="summary-card" 
              :class="{ 'selected-card': selectedCard === 'fulfillments' }"
              @click="selectCard('fulfillments')"
            >
              <v-card-text class="text-center">
                <div class="text-h6 font-weight-medium mb-2">Total Fulfillments</div>
                <div class="text-h3 font-weight-bold primary--text" :class="{ blurred: !has_analytics }">{{ fulfillments.length }}</div>
                <div class="expand-hint" :class="{ 'is-open': selectedCard === 'fulfillments' }">
                  <span>{{ selectedCard === 'fulfillments' ? 'Hide details' : 'View details' }}</span>
                  <v-icon small color="primary">mdi-chevron-down</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card 
              rounded="lg" 
              elevation="2" 
              class="summary-card" 
              :class="{ 'selected-card': selectedCard === 'picks' }"
              @click="selectCard('picks')"
            >
              <v-card-text class="text-center">
                <div class="text-h6 font-weight-medium mb-2">Total Picks</div>
                <div class="text-h3 font-weight-bold primary--text" :class="{ blurred: !has_analytics }">{{ totalPicks }}</div>
                <div class="expand-hint" :class="{ 'is-open': selectedCard === 'picks' }">
                  <span>{{ selectedCard === 'picks' ? 'Hide details' : 'View details' }}</span>
                  <v-icon small color="primary">mdi-chevron-down</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card 
              rounded="lg" 
              elevation="2" 
              class="summary-card" 
              :class="{ 'selected-card': selectedCard === 'timeSpent' }"
              @click="selectCard('timeSpent')"
            >
              <v-card-text class="text-center">
                <div class="text-h6 font-weight-medium mb-2">Total Time Spent</div>
                <div class="text-h3 font-weight-bold primary--text" :class="{ blurred: !has_analytics }">{{ formatTime(totalTimeSpent) }}</div>
                <div class="expand-hint" :class="{ 'is-open': selectedCard === 'timeSpent' }">
                  <span>{{ selectedCard === 'timeSpent' ? 'Hide details' : 'View details' }}</span>
                  <v-icon small color="primary">mdi-chevron-down</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Detailed Insights Card -->
        <v-card 
          v-if="selectedCard" 
          rounded="lg" 
          elevation="2" 
          class="mb-4 pa-4 detailed-insights-card"
        >
          <v-card-title class="text-h6 font-weight-medium">
            {{ detailedInsightsTitle }}
          </v-card-title>
          
          <!-- Total Picks Insights -->
          <v-card-text v-if="selectedCard === 'picks'">
            <div class="tag-chips-container">
              <v-chip
                v-for="(tag, i) in productTagsData"
                :key="'tag' + i"
                class="ma-1"
                :color="getTagColor(i)"
                label
                outlined
              >
                <span class="font-weight-medium">{{ tag.tag }}</span>
                <v-chip
                  class="ml-2"
                  :class="{ blurred: !has_analytics }"
                  x-small
                  :color="getTagColor(i)"
                  dark
                >
                  {{ tag.count }}
                </v-chip>
              </v-chip>
              
              <div v-if="productTagsData.length === 0" class="text-center grey--text text--darken-1 py-4">
                No product tags found in the selected date range.
              </div>
            </div>
          </v-card-text>
          
          <!-- Time Spent Insights -->
          <v-card-text v-else-if="selectedCard === 'timeSpent'">
            <v-row>
              <v-col cols="12" md="4">
                <v-card 
                  outlined 
                  class="pa-3 text-center insight-card" 
                  :class="{ 'selected-insight-card': selectedInsightCard === 'timePerOrder' }"
                  @click="selectInsightCard('timePerOrder')"
                >
                  <div class="text-subtitle-1 font-weight-medium mb-2">Time per Order</div>
                  <div class="text-h5 font-weight-bold" :class="{ blurred: !has_analytics }">{{ formatTime(timePerOrder) }}</div>
                  <div class="text-caption">Average time spent per parcel.</div>
                  <div class="expand-hint" :class="{ 'is-open': selectedInsightCard === 'timePerOrder' }">
                    <span>{{ selectedInsightCard === 'timePerOrder' ? 'Hide breakdown' : 'Per user breakdown' }}</span>
                    <v-icon small color="primary">mdi-chevron-down</v-icon>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card 
                  outlined 
                  class="pa-3 text-center insight-card" 
                  :class="{ 'selected-insight-card': selectedInsightCard === 'timePerPick' }"
                  @click="selectInsightCard('timePerPick')"
                >
                  <div class="text-subtitle-1 font-weight-medium mb-2">Time per Pick</div>
                  <div class="text-h5 font-weight-bold" :class="{ blurred: !has_analytics }">{{ formatTime(timePerPick) }}</div>
                  <div class="text-caption">Average time picking items, excluding packing.</div>
                  <div class="expand-hint" :class="{ 'is-open': selectedInsightCard === 'timePerPick' }">
                    <span>{{ selectedInsightCard === 'timePerPick' ? 'Hide breakdown' : 'Per user breakdown' }}</span>
                    <v-icon small color="primary">mdi-chevron-down</v-icon>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card 
                  outlined 
                  class="pa-3 text-center insight-card" 
                  :class="{ 'selected-insight-card': selectedInsightCard === 'timePerParcel' }"
                  @click="selectInsightCard('timePerParcel')"
                >
                  <div class="text-subtitle-1 font-weight-medium mb-2">Time per Parcel</div>
                  <div class="text-h5 font-weight-bold" :class="{ blurred: !has_analytics }">{{ formatTime(timePerParcel) }}</div>
                  <div class="text-caption">Average time packing parcels, excluding picking items</div>
                  <div class="expand-hint" :class="{ 'is-open': selectedInsightCard === 'timePerParcel' }">
                    <span>{{ selectedInsightCard === 'timePerParcel' ? 'Hide breakdown' : 'Per user breakdown' }}</span>
                    <v-icon small color="primary">mdi-chevron-down</v-icon>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            
            <!-- Per-User Breakdown (when an insight card is selected) -->
             <v-card
                v-if="loading_insight_card"
                >
              <v-progress-circular></v-progress-circular>
              </v-card>
            <v-card 
              v-if="selectedInsightCard" 
              outlined 
              class="mt-4 pa-4 user-breakdown-card"
            >
              <v-card-title class="text-subtitle-1 font-weight-medium pb-2">
                {{ selectedInsightCardTitle }} - Per User Breakdown
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col 
                    v-for="(value, userId) in userBreakdownData" 
                    :key="userId" 
                    cols="12" 
                    sm="6" 
                    md="4" 
                    lg="3"
                  >
                    <v-card outlined class="pa-3 text-center">
                      <div class="text-subtitle-2 font-weight-medium mb-1">{{ (company_users.find(c => c.id === userId) || {}).name || (userId.startsWith('shopify:') ? 'Shopify admin' : userId) }}</div>
                      <div class="text-h6 font-weight-bold" :class="{ blurred: !has_analytics }">{{ formatTime(value) }}</div>
                    </v-card>
                  </v-col>
                  
                  <v-col v-if="Object.keys(userBreakdownData).length === 0" cols="12">
                    <div class="text-center grey--text text--darken-1 py-3">
                      No user data available for this metric.
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-card-text>
          
          <!-- Fulfillments Insights (blank for now) -->
          <v-card-text v-else-if="selectedCard === 'fulfillments'">
            <div class="text-body-1 grey--text text--darken-1 text-center py-5">
              Detailed insights for fulfillments will be available soon.
            </div>
          </v-card-text>
        </v-card>

        <!-- Data table -->
        <v-card rounded="lg" elevation="2" class="data-table-card" :class="{ 'locked-table': !has_analytics }">
          <div v-if="!has_analytics" class="locked-overlay">
            <v-icon large color="primary" class="mb-2">mdi-lock-outline</v-icon>
            <div class="text-subtitle-1 font-weight-medium mb-3">Upgrade plan to access analytics</div>
            <v-btn color="primary" depressed to="/auth/plans">Upgrade plan</v-btn>
          </div>
          <v-data-table
            :headers="headers"
            :items="table_items"
            item-key="sf_fulfillment_id"
            :options.sync="options"
            :items-per-page="10"
            :footer-props="footer_props"
            :disable-sort="!has_analytics"
            :hide-default-footer="!has_analytics"
            :item-class="rowClass"
            class="fulfillments-table"
          >
            <template v-slot:[`item.created_at`]="{ item }">
              {{ item.created_at | DateFilter }}
            </template>
            <template v-slot:[`item.picks`]="{ item }">
              {{ calculatePicks(item) }}
            </template>
            <template v-slot:[`item.seconds_spent`]="{ item }">
              {{ formatTime(calculateTimeSpent(item)) }}
            </template>
            <template v-slot:[`item.weight_in_grams`]="{ item }">
              {{ item.weight_in_grams || 0 }} g
            </template>
          </v-data-table>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { DateFilter } from '@/utils/filters'
import { getFirestore, collection, query, where, orderBy, getDocs } from 'firebase/firestore'

export default {
  filters: {
    DateFilter
  },
  data() {
    return {
      loading_insight_card: false,
      loading: true,
      selected_data_source_id: '',
      selected_date_range: 'last30days',
      date_ranges: [
        { text: 'Last 7 Days', value: 'last7days' },
        { text: 'Last 30 Days', value: 'last30days' },
        { text: 'Month to Date', value: 'monthToDate' },
        { text: 'Last Month', value: 'lastMonth' },
        { text: 'Custom Range', value: 'custom' }
      ],
      customStartDate: null,
      customEndDate: null,
      datePickerMenu1: false,
      datePickerMenu2: false,
      fulfillments: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['created_at'],
        sortDesc: [true]
      },
      footer_props: {
        'items-per-page-options': [5, 10, 25, 50, 100]
      },
      selectedCard: null,
      selectedInsightCard: null, // Track which insight card is selected
      headers: [
        {
          text: 'Date',
          align: 'start',
          sortable: true,
          value: 'created_at',
        },
        {
          text: 'Order',
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Customer',
          align: 'start',
          sortable: true,
          value: 'shipping_address.name',
        },
        {
          text: 'Destination',
          align: 'start',
          sortable: false,
          value: 'shipping_address.country_code',
        },
        {
          text: 'Picks',
          align: 'center',
          sortable: false,
          value: 'picks',
        },
        {
          text: 'Time Spent',
          align: 'center',
          sortable: false,
          value: 'seconds_spent',
        },
        {
          text: 'Shipping method',
          align: 'center',
          sortable: false,
          value: 'shipping_method',
        },
        {
          text: 'Shipping fee',
          align: 'center',
          sortable: false,
          value: 'shipping_fee.amount',
        },
        {
          text: 'Shipping fee currency',
          align: 'center',
          sortable: false,
          value: 'shipping_fee.currency_code',
        },
        {
          text: 'Weight',
          align: 'center',
          sortable: true,
          value: 'weight_in_grams',
        }
      ]
    }
  },
  computed: {
    ...mapState({
      company: state => state.warehouse.company,
      company_users: state => state.user.company_users
    }),
    ...mapGetters({
      get_product: 'product/get_product',
      get_plan: 'warehouse/get_plan',
    }),
    has_analytics() {
      return !!this.get_plan?.analytics
    },
    // Without analytics: show a short, fixed preview (already sorted newest first by the query)
    table_items() {
      return this.has_analytics ? this.fulfillments : this.fulfillments.slice(0, 8)
    },
    storeOptions() {
      if (!this.company.data_sources || this.company.data_sources.length < 2) {
        return this.company.data_sources || []
      }
      
      // Add "All stores" option when there are 2 or more stores
      return [
        { id: 'all', name: 'All stores' },
        ...this.company.data_sources
      ]
    },
    totalPicks() {
      return this.fulfillments.reduce((total, item) => total + this.calculatePicks(item), 0)
    },
    totalTimeSpent() {
      return this.fulfillments.reduce((total, item) => total + this.calculateTimeSpent(item), 0)
    },
    isCustomDateRangeValid() {
      return this.customStartDate && this.customEndDate && 
             new Date(this.customStartDate) <= new Date(this.customEndDate)
    },
    detailedInsightsTitle() {
      switch (this.selectedCard) {
        case 'fulfillments':
          return 'Fulfillment Details'
        case 'picks':
          return 'Product Tag Breakdown'
        case 'timeSpent':
          return 'Time Analysis'
        default:
          return 'Detailed Insights'
      }
    },
    productTagsData() {
      if (!this.fulfillments.length) return []
      
      // Create a map to count product tags
      const tagCounts = new Map()
      
      this.fulfillments.forEach(fulfillment => {
        if (fulfillment.line_items && fulfillment.line_items.length) {
          fulfillment.line_items.forEach(lineItem => {
            if (lineItem.tags && lineItem.tags.length) {
              lineItem.tags.forEach(tag => {
                const count = tagCounts.get(tag) || 0
                tagCounts.set(tag, count + (lineItem.quantity || 1))
              })
            }
          })
        }
      })
      
      // Convert map to array for data table
      return Array.from(tagCounts.entries()).map(([tag, count]) => ({
        tag,
        count
      })).sort((a, b) => b.count - a.count) // Sort by count descending
    },
    timePerOrder() {
      if (!this.fulfillments.length) return 0
      return Math.round(this.totalTimeSpent / this.fulfillments.length)
    },
    timePerPick() {
      if (!this.totalPicks) return 0
      return Math.round(
        this.fulfillments.reduce(
          (total, item) => total + item.line_items.reduce((t, i) => t + (i.sf_seconds_spent || 0), 0), 
          0
        ) / this.totalPicks
      )
    },
    timePerParcel() {
      const fulfillments = this.fulfillments
      if (!fulfillments.length) return 0
      // Calculate total seconds spent on parcels
      const totalParcelSeconds = fulfillments
        .reduce((total, item) => {
        return total + (item.sf_seconds_spent || 0)
      }, 0)
      
      return Math.round(totalParcelSeconds / fulfillments.length)
    },
    selectedInsightCardTitle() {
      switch (this.selectedInsightCard) {
        case 'timePerOrder':
          return 'Time per Order'
        case 'timePerPick':
          return 'Time per Pick'
        case 'timePerParcel':
          return 'Time per Parcel'
        default:
          return 'Time Analysis'
      }
    },
    userBreakdownData() {
      if (!this.fulfillments.length || !this.selectedInsightCard) return {}
      
      // Create a map to store user data
      const userMap = new Map()
      
      // Process fulfillments based on the selected insight card
      this.fulfillments.forEach(fulfillment => {
        // Skip if no user ID
        if (!fulfillment.sf_fulfilled_by) return
        
        const userId = fulfillment.sf_fulfilled_by
        
        switch (this.selectedInsightCard) {
          case 'timePerOrder': {
            // Calculate total time spent on this order by this user
            const totalOrderTime = this.calculateTimeSpent(fulfillment)
            
            // Add to user's total and count
            if (!userMap.has(userId)) {
              userMap.set(userId, { total: 0, count: 0 })
            }
            
            const orderData = userMap.get(userId)
            orderData.total += totalOrderTime
            orderData.count += 1
            break
          }
            
          case 'timePerPick': {
            // Skip if no line items
            if (!fulfillment.line_items || !fulfillment.line_items.length) return
            
            // Calculate total pick time and count for this fulfillment
            const pickTime = fulfillment.line_items.reduce((total, item) => total + (item.sf_seconds_spent || 0), 0)
            const pickCount = this.calculatePicks(fulfillment)
            
            // Skip if no picks
            if (pickCount === 0) return
            
            // Add to user's total and count
            if (!userMap.has(userId)) {
              userMap.set(userId, { total: 0, count: 0 })
            }
            
            const pickData = userMap.get(userId)
            pickData.total += pickTime
            pickData.count += pickCount
            break
          }
            
          case 'timePerParcel': {
            // Calculate parcel time (excluding line items)
            const parcelTime = fulfillment.sf_seconds_spent || 0
            
            // Skip if no parcel time
            if (parcelTime === 0) return
            
            // Add to user's total and count
            if (!userMap.has(userId)) {
              userMap.set(userId, { total: 0, count: 0 })
            }
            
            const parcelData = userMap.get(userId)
            parcelData.total += parcelTime
            parcelData.count += 1
            break
          }
        }
      })
      
      // Calculate averages and return as object
      const result = {}
      userMap.forEach((data, userId) => {
        if (data.count > 0) {
          result[userId] = Math.round(data.total / data.count)
        }
      })
      
      return result
    },
    dateRange() {
      const now = new Date()
      let startDate, endDate = now
      // endDate.setHours(endDate.getHours() + 4)

      switch (this.selected_date_range) {
        case 'last7days':
          startDate = new Date(now)
          startDate.setDate(now.getDate() - 7)
          break
        case 'last30days':
          startDate = new Date(now)
          startDate.setDate(now.getDate() - 30)
          break
        case 'monthToDate':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1)
          break
        case 'lastMonth':
          endDate = new Date(now.getFullYear(), now.getMonth(), 0)
          startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1)
          break
        case 'custom':
          if (this.customStartDate && this.customEndDate) {
            startDate = new Date(this.customStartDate)
            startDate.setHours(0, 0, 0, 0)
            endDate = new Date(this.customEndDate)
            endDate.setHours(23, 59, 59, 999)
          } else {
            startDate = new Date(now)
            startDate.setDate(now.getDate() - 30)
          }
          break
        default:
          startDate = new Date(now)
          startDate.setDate(now.getDate() - 30)
      }
      
      // Format dates to match Firestore's format
      // This ensures timezone consistency for proper filtering
      const formatDateForFirestore = (date) => {
        // Create an ISO string but remove the 'Z' to make it timezone-agnostic
        // Firestore will interpret this correctly regardless of the client's timezone
        return date.toISOString() // .replace('Z', '')

      }
      
      const formattedStartDate = formatDateForFirestore(startDate)
      const formattedEndDate = formatDateForFirestore(endDate)
      
      console.log({
        startDate: formattedStartDate,
        endDate: formattedEndDate
      })
      
      return {
        startDate: formattedStartDate,
        endDate: formattedEndDate
      }
    }
  },
  methods: {
    handleDateRangeChange() {
      if (this.selected_date_range === 'custom') {
        // Set default dates if not already set
        if (!this.customStartDate) {
          const defaultStart = new Date()
          defaultStart.setDate(defaultStart.getDate() - 7)
          this.customStartDate = defaultStart.toISOString().substr(0, 10)
        }
        if (!this.customEndDate) {
          this.customEndDate = new Date().toISOString().substr(0, 10)
        }
      } else {
        this.fetchData()
      }
    },
    validateAndFetch() {
      if (this.isCustomDateRangeValid) {
        this.fetchData()
      }
    },
    rowClass(item) {
      if (this.has_analytics) return ''
      return item === this.table_items[0] ? '' : 'blurred-row'
    },
    selectCard(cardType) {
      // Toggle card selection
      this.selectedCard = this.selectedCard === cardType ? null : cardType
      
      // Reset insight card selection when changing main card
      this.selectedInsightCard = null
    },
    async selectInsightCard(cardType) {
      this.loading_insight_card = true
      await this.$store.dispatch('user/get_company_users')
      // Toggle insight card selection
      this.selectedInsightCard = this.selectedInsightCard === cardType ? null : cardType
      this.loading_insight_card = false
    },
    getTagColor(index) {
      // Array of colors to cycle through for the tags
      const colors = [
        'primary', 'secondary', 'success', 'info', 'warning', 
        'error', 'purple', 'indigo', 'cyan', 'teal', 'orange'
      ];
      return colors[index % colors.length];
    },
    getProductTags(item){
      console.log(item)
      // return item.line_items
      return ''
    },
    calculatePicks(item) {
      if (!item.line_items || !item.line_items.length) return 0
      
      return item.line_items.reduce((total, lineItem) => {
        return total + (lineItem.quantity || 0)
      }, 0)
    },
    calculateTimeSpent(item) {
      let totalSeconds = item.sf_seconds_spent || 0
      
      if (item.line_items && item.line_items.length) {
        totalSeconds += item.line_items.reduce((total, lineItem) => {
          return total + (lineItem.sf_seconds_spent || 0)
        }, 0)
      }
      
      return Math.round(totalSeconds)
    },
    formatTime(seconds) {
      if (!seconds) return '0s'
      
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      
      if (minutes === 0) {
        return `${remainingSeconds}s`
      } else if (remainingSeconds === 0) {
        return `${minutes}m`
      } else {
        return `${minutes}m ${remainingSeconds}s`
      }
    },
    async fetchData() {
      try {
        await this.$store.dispatch('product/get_products', { company_uid: this.company.id })
        this.loading = true
        
        // Clear previous data
        this.fulfillments = []
        
        if (!this.selected_data_source_id) {
          this.loading = false
          return
        }
        
        const db = getFirestore()
        
        // Log the date range we're using for debugging
        console.log('Querying with date range:', {
          startDate: this.dateRange.startDate,
          endDate: this.dateRange.endDate
        })
        
        let allFulfillments = []
        
        if (this.selected_data_source_id === 'all') {
          // Query all data sources
          console.log('Querying all stores for fulfillments')
          
          const promises = this.company.data_sources.map(async (dataSource) => {
            const fulfillmentsRef = collection(db, 'data_sources', dataSource.id, 'fulfillments')
            
            const q = query(
              fulfillmentsRef,
              where('created_at', '>=', this.dateRange.startDate),
              where('created_at', '<=', this.dateRange.endDate),
              orderBy('created_at', 'desc')
            )
            
            const snapshot = await getDocs(q)
            
            return snapshot.docs.map(d => {
              const data = d.data()
              return {
                ...data,
                line_items: data.line_items.map(item => {
                  const uuid = `${dataSource.parent_company_id}:${dataSource.id}:${item.product_id}:${item.variant_id}`
                  const product = this.get_product(uuid);
                  if(!product) console.log('no product')
                  return {
                    ...item,
                    tags: product?.tags || [],
                  }
                }),
                sf_fulfillment_id: d.id,
                sf_data_source_id: dataSource.id, // Add data source ID for reference
                sf_data_source_name: dataSource.name, // Add data source name for reference
                weight_in_grams: data.weight_in_grams || data.line_items.reduce((tot, i) => tot + (i.grams * i.quantity),0) || 0
              }
            })
          })
          
          const results = await Promise.all(promises)
          allFulfillments = results.flat()
          
          // Sort all fulfillments by created_at descending
          allFulfillments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          
        } else {
          // Query single data source
          const fulfillmentsRef = collection(db, 'data_sources', this.selected_data_source_id, 'fulfillments')
          
          const q = query(
            fulfillmentsRef,
            where('created_at', '>=', this.dateRange.startDate),
            where('created_at', '<=', this.dateRange.endDate),
            orderBy('created_at', 'desc')
          )
          
          console.log('Executing Firestore query with date filters')
          
          const snapshot = await getDocs(q)
          
          // Process the results
          allFulfillments = snapshot.docs.map(d => {
            const data = d.data()
            // Log the first few fulfillment timestamps to see their format
            if (allFulfillments.length < 3) {
              console.log('Fulfillment timestamp format:', data.created_at)
            }
            
            return {
              ...data,
              line_items: data.line_items.map(item => {
                const data_source = this.company.data_sources.find(ds => ds.id === this.selected_data_source_id)
                const uuid = `${data_source.parent_company_id}:${data_source.id}:${item.product_id}:${item.variant_id}`
                const product = this.get_product(uuid);
                if(!product) console.log('no product')
                return {
                  ...item,
                  tags: product?.tags || [],
                }
              }),
              sf_fulfillment_id: d.id,
              weight_in_grams: data.weight_in_grams || data.line_items.reduce((tot, i) => tot + (i.grams * i.quantity),0) || 0
            }
          })
        }
        
        this.fulfillments = allFulfillments
        console.log(`Loaded ${this.fulfillments.length} fulfillments`)
        
        // Data loaded successfully
      } catch (error) {
        console.error('Error fetching report data:', error)
      } finally {
        this.loading = false
      }
    },
    // Chart methods removed for now
    downloadCSV() {
      if (!this.fulfillments.length || !this.has_analytics) return
      
      // Prepare CSV content
      const headers = [
        'Date',
        'Order',
        'Customer',
        'Destination',
        'Picks',
        'Time Spent (seconds)',
        'Shipping method',
        'Shipping fee',
        'Shipping fee currency',
        'Weight (g)',
        'Product tags'
      ]
      
      const rows = this.fulfillments.map(item => [
        new Date(item.created_at).toLocaleDateString(),
        item.name,
        item.shipping_address?.name || '',
        item.shipping_address?.country_code || '',
        this.calculatePicks(item),
        this.calculateTimeSpent(item),
        item.shipping_method || '',
        item.shipping_fee?.amount || '', 
        item.shipping_fee?.currency_code || '', 
        item.weight_in_grams,
        `"${item.line_items.reduce((tot, line_item) => tot.concat(line_item.tags || []), []).join(',')}"`
      ])
      
      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n')
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `fulfillments-${new Date().toISOString().substring(0, 10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
  async mounted() {
    // Set default data source
    if (this.company.data_sources && this.company.data_sources.length > 0) {
      // If there are multiple stores, default to "All stores", otherwise use the first store
      if (this.company.data_sources.length >= 2) {
        this.selected_data_source_id = 'all'
      } else {
        this.selected_data_source_id = this.company.data_sources[0].id
      }
    }
    // Fetch initial data
    this.fetchData()
  }
}
</script>

<style lang="scss" scoped>
.reports-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.reports-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.summary-card {
  height: 100%;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  }
  
  &.selected-card {
    border: 2px solid var(--v-primary-base);
  }
}

.insight-card {
  height: 100%;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  }
  
  &.selected-insight-card {
    border: 2px solid var(--v-primary-base);
  }
}

.expand-hint {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--v-primary-base);
  opacity: 0.8;
  transition: opacity 0.2s ease;

  .v-icon {
    transition: transform 0.2s ease;
  }

  &.is-open .v-icon {
    transform: rotate(180deg);
  }
}

.summary-card:hover .expand-hint,
.insight-card:hover .expand-hint {
  opacity: 1;
}

.tag-chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
}

.data-table-card {
  flex: 1;
}

.blurred {
  filter: blur(8px);
  user-select: none;
  pointer-events: none;
}

.locked-table {
  position: relative;
  overflow: hidden;

  ::v-deep .blurred-row td {
    filter: blur(5px);
    user-select: none;
    pointer-events: none;
  }
}

.locked-overlay {
  position: absolute;
  top: 96px; // below the table header and the first (visible) row
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.9) 40%, rgba(255, 255, 255, 0.95) 100%);
}
</style>
